import { observable, action, toJS, isObservableArray } from "mobx";
import { Validator, IValidatorOpts } from "./_internal";

// Callback
export type Callback = () => void;

// IObservableStoreOpts
export interface IObservableStoreOpts<D extends {} = any> extends Partial<IValidatorOpts<Partial<D>>> {
	deep?: boolean;
}

/**
 * Observable store
 */
export class ObservableStore<D extends {}, N extends ObservableStore<D, any> = ObservableStore<D, any>> {
	// Default data
	public defaults: D;

	// Data storage
	public readonly data: D;

	// Validator
	protected __validation?: Validator<Partial<D>>;

	// On before change
	public onBeforeChange?(data: D): void;

	/**
	 * Class constructor
	 * @param data
	 * @param opts
	 */
	public constructor(data: D, protected readonly opts?: IObservableStoreOpts) {
		opts || (opts = {});
		typeof opts.deep === "boolean" || (opts.deep = false);
		this.data = observable.object(data, {}, { deep: opts.deep });
		if (!this.defaults) this.defaults = data;
		if (opts.rules) this.__validation = new Validator(opts as IValidatorOpts<Partial<D>>);
	}

	/**
	 * Set validator
	 * @param validator
	 */
	public setValidator(validator: Validator<Partial<D>>) {
		this.__validation = validator;
	}

	/**
	 * Key value getter
	 * @param key
	 * @returns
	 */
	public get<T = any>(key: keyof D): T {
		return this.data[key] as unknown as T;
	}

	/**
	 * Set single key value
	 * @param key
	 * @param value
	 */
	@action.bound public set(key: keyof D, value: any, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		this.data[key] = value;
		this.__validation?.validate(key, value, this.data);
		cb && cb();
	}

	/**
	 * Set multiple keys values
	 * @param patch
	 */
	@action.bound public mset(patch: Partial<D>, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		for (let key in patch) {
			this.data[key] = patch[key];
			this.__validation?.validate(key, patch[key], this.data);
		}
		cb && cb();
	}

	/**
	 * Delete key
	 * @param key
	 */
	@action.bound public del(key: keyof D, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		delete this.data[key];
		cb && cb();
	}

	/**
	 * Increment
	 * @param key
	 * @param cb
	 */
	@action.bound public inc(key: keyof D, cb?: Callback) {
		if (typeof this.data[key] !== "number") {
			console.warn("Attempting to increment a non-numeric value, key:", key, "value:", this.data[key]);
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			++(this.data as any)[key];
			this.__validation?.validate(key, (this.data as any)[key], this.data);
		}

		cb && cb();
	}

	/**
	 * Decrement
	 * @param key
	 * @param cb
	 */
	@action.bound public dec(key: keyof D, cb?: Callback) {
		if (typeof this.data[key] !== "number") {
			console.warn("Attempting to decrement a non-numeric value, key:", key, "value:", this.data[key]);
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			--(this.data as any)[key];
			this.__validation?.validate(key, (this.data as any)[key], this.data);
		}

		cb && cb();
	}

	/**
	 * Toggle key value
	 * @param key
	 */
	@action.bound public toggle(key: keyof D, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);
		const value = this.data[key];

		if (typeof value !== "boolean" && typeof value !== "number") {
			console.warn("Attempting to toggle a non-boolean (or non-numeric) value, key:", key, "value:", value);
		}

		const _value = typeof value === "number" ? (value === 0 ? 1 : 0) : (!value as any);

		this.data[key] = _value;
		this.__validation?.validate(key, _value, this.data);

		cb && cb();
	}

	/**
	 * Toggle indetermiante
	 * @param key
	 * @param cb
	 */
	@action.bound public toggleWithIndeterminate(key: keyof D, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);
		const value = this.data[key];

		if (typeof value !== "boolean" && typeof value !== "undefined") {
			console.warn("Attempting to toggle with indeterminate a non-boolean (or non-undefined) value, key:", key, "value:", value);
		}

		switch (value) {
			case true:
				this.set(key, false);
				break;
			case false:
				this.del(key);
				break;
			default:
				this.set(key, true);
		}

		cb && cb();
	}

	/**
	 * Toggle multiple keys values
	 * @param patch
	 */
	@action.bound public mtoggle(patch: Partial<D>, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		for (let key in patch) {
			const value = this.data[key];

			if (typeof value !== "boolean" && typeof value !== "number") {
				console.warn("Attempting to toggle a non-boolean (or non-numeric) value, key:", key, "value:", value);
			}
			this.data[key] = typeof value === "number" ? (value === 0 ? 1 : 0) : (!value as any);
		}
		cb && cb();
	}

	/**
	 * Push
	 * @param key
	 * @param value
	 * @param cb
	 */
	@action.bound public push(key: keyof D, value: any, cb?: (length: number) => void) {
		const arr = this.data[key] as unknown as any[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			const r = arr.push(value);
			cb && cb(r);
			return r;
		}
	}

	/**
	 * Pop
	 * @param key
	 * @param cb
	 * @returns
	 */
	@action.bound public pop<T = any>(key: keyof D, cb?: (item: T) => void) {
		const arr = this.data[key] as unknown as T[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			const r = arr.pop() as T;
			cb && cb(r);
			return r;
		}
	}

	/**
	 * Shift
	 * @param key
	 * @param cb
	 * @returns
	 */
	@action.bound public shift<T = any>(key: keyof D, cb?: (item: T) => void) {
		const arr = this.data[key] as unknown as T[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);
			const r = arr.shift() as T;
			cb && cb(r);
			return r;
		}
	}

	/**
	 * Unshift
	 * @param key
	 * @param items
	 * @param cb
	 * @returns
	 */
	@action.bound public unshift<T = any>(key: keyof D, items: T[], cb?: (length: number) => void) {
		const arr = this.data[key] as unknown as T[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			const r = arr.unshift(...items);
			cb && cb(r);
			return r;
		}
	}

	/**
	 * Splice
	 * @param key
	 * @param value
	 * @param index
	 * @param cb
	 */
	@action.bound public splice<T = any>(key: keyof D, index: number, delCount: number, items: T[], cb?: (removed?: T[]) => void): T[] {
		const arr = this.data[key] as unknown as T[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);

			const r = arr.splice(index, delCount, ...items) as unknown as T[];
			cb && cb(r);
			return r;
		}
	}

	/**
	 * Remove item from array
	 * @param Value
	 * @param items
	 * @param cb
	 */
	@action.bound public removeItem(key: keyof D, value: any, cb?: (removed?: number | void) => void): number | void {
		const arr = this.data[key] as unknown as any[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			const index = arr.indexOf(value);

			if (index === -1) {
				cb && cb();
			} else {
				this.onBeforeChange && this.onBeforeChange(this.data);

				arr.splice(index, 1);
				cb && cb(index);
			}
		}
	}

	/**
	 * Remove all items
	 * @param key
	 * @param cb
	 */
	@action.bound public removeAllItems(key: keyof D, cb?: () => void) {
		const arr = this.data[key] as unknown as any[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		}
		this.onBeforeChange && this.onBeforeChange(this.data);
		(this.data[key] as unknown as any[]).length = 0;
		cb && cb();
	}

	/**
	 * Add or remove value
	 * @param sIndex
	 * @param dIndex
	 * @param cb
	 */
	@action.bound public addOrRemoveValue(key: keyof D, value: any, cb?: (removed?: boolean) => void) {
		const arr = this.data[key] as unknown as any[];
		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		} else {
			this.onBeforeChange && this.onBeforeChange(this.data);
			const index = arr.indexOf(value);

			if (index === -1) {
				this.push(key, value, () => cb && cb(false));
			} else {
				this.splice(key, index, 1, [], () => cb && cb(true));
			}
		}
	}

	/**
	 * Swap items
	 * @param key
	 * @param sIndex
	 * @param dIndex
	 * @param cb
	 * @returns
	 */
	@action.bound public swap<T = any>(key: keyof D, sIndex: number, dIndex: number, cb?: (arr?: T[]) => void): T[] {
		const arr = this.data[key] as unknown as T[];

		if (!isObservableArray(arr)) {
			console.error("Value is not an observable array");
			return void 0;
		}
		this.onBeforeChange && this.onBeforeChange(this.data);

		const b = arr[sIndex];
		arr[sIndex] = arr[dIndex];
		arr[dIndex] = b;

		cb && cb(arr);
		return arr;
	}

	/**
	 * Similar to mset but remove unset properties
	 * @param patch
	 */
	@action public loadData(data: Partial<D>, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		for (let key in this.data) {
			if (typeof data[key] === "undefined") {
				delete this.data[key];
			}
		}

		for (let key in data) {
			this.data[key] = data[key];
			this.__validation?.validate(key, data[key], this.data);
		}
		cb && cb();
	}

	/**
	 * Clone
	 * @returns
	 */
	public clone(): N {
		return new (this.constructor as any)(this.toJS(), this.opts);
	}

	/**
	 * Validation getter
	 */
	public get validation(): Validator<Partial<D>> {
		return this.__validation;
	}

	/**
	 * Load defaults
	 * @param cb
	 */
	public loadDefaults(cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);
		this.mset(this.defaults, cb);
	}

	/**
	 * To JS
	 * @returns
	 */
	public toJS() {
		return toJS(this.data);
	}
}
